import {
  trackApplyStandardsApplied,
  trackUpdateStandardsApplied,
} from '@src/analytics/EventTracker';
import { PARAGRAPH_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import {
  NoShowcaseMessage,
  SaveButton as SaveButtonKaraokeBase,
} from '@src/components/sidepanel/KaraokeSettings/KaraokeSaveOrCancelTemplate';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { InfoIconSmall } from '@src/svgs/InfoIconSmall';
import {
  applyControlOverrides,
  applyLabelOverridesToSource,
  applyTemplateStandardsToElement,
  buildApplyStandardsRows,
} from '@src/utility/compositionSettings';
import {
  getElementMetaObject,
  upsertElementMetaObject,
} from '@src/utility/extraElementData';
import { applySourcePropertyOverrides } from '@src/utility/standardsSourceProperties';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { useCompositionContext } from '../CompositionContext';
import { SaveNameModal } from '../SaveNameModal';
import { ApplyStandardsModal } from './ApplyStandardsModal';
import { UpdateStandardsModal } from './UpdateStandardsModal';

import type { CompositionActions } from '../CompositionContext';
import type {
  AspectConfigKey,
  AspectConfiguration,
  CompositionTemplate,
  ControlConfiguration,
} from './types';
import type { PlacementParts } from './UpdateStandardsModal';
import type { ElementState } from '@src/renderer/ElementState';
import type {
  ControlOverride,
  EditorStoreLike,
} from '@src/utility/compositionSettings';
import type {
  ElementLayoutReadout,
  ElementPlacementParts,
} from '@src/utility/quoteCardLayout';
import type { SourcePropertyOverride } from '@src/utility/standardsSourceProperties';

const UPDATE_CLICK_DELAY = 750;

export const CompositionSaveTemplate = ({
  composition,
  updatedValues,
  categoryId,
  subcategoryId,
  activeElement,
  editorStore,
  saveMeta = true,
  onAfterCreate,
  onAfterSave,
  positionDirty,
  getCurrentAspectPlacement,
  getSourceForSave,
  elementLayout,
  standardSource,
}: {
  composition: CompositionTemplate;
  updatedValues: {
    elementPaths: number[][];
    value: unknown;
    property: string;
  }[];
  categoryId: string;
  subcategoryId: string;
  activeElement: ElementState;
  editorStore?: EditorStoreLike;
  saveMeta?: boolean;
  onAfterCreate?: (response: CompositionTemplate) => void;
  onAfterSave?: () => void;
  positionDirty?: boolean;
  getCurrentAspectPlacement?: () =>
    | { key: AspectConfigKey; config: AspectConfiguration }
    | undefined;
  /**
   * The source to write for this save. Pull Quotes merge only the moved
   * element geometry into the template's own source (PLAT-11977); without it
   * `composition.source` is written as-is (Creator Studio).
   */
  getSourceForSave?: (
    elementParts: ElementPlacementParts,
    action: CompositionActions,
  ) => CompositionTemplate['source'];
  /** Every element's position and size, template vs card (Pull Quotes). */
  elementLayout?: ElementLayoutReadout[];
  /** The template's own source when `composition.source` is the card on
   *  screen (Pull Quotes), for the modal's More fields. */
  standardSource?: CompositionTemplate['source'];
}) => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const { enableApplyStandardsPreview } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const [updating, setUpdating] = useState<Date | null>(null);
  const [modalAction, setModalAction] = useState<CompositionActions | null>(
    null,
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  const meta = getElementMetaObject(
    videoCreator.currentVideo,
    activeElement.source.id,
  );

  const { addUpdateFavoriteCompositionTemplate } = useCompositionContext();
  const handleSaveTemplate = async (
    action: CompositionActions,
    newCompositionName: string,
    overrideValues: ControlOverride[] = updatedValues,
    placementParts: PlacementParts = { position: true, scale: true },
    elementParts: ElementPlacementParts = {},
    sourceOverrides: SourcePropertyOverride[] = [],
  ) => {
    if (!videoCreator.organization || !newCompositionName) return;
    setModalAction(null);
    const updateStart = new Date();
    if (action === 'update') {
      setUpdating(updateStart);
    }

    // Only controls with a defined override value change; the rest keep the
    // template's saved standard verbatim (PLAT-11977).
    const updatedControls: ControlConfiguration[] = applyControlOverrides(
      JSON.parse(JSON.stringify(composition.controls)),
      overrideValues,
    );

    // Label edits rename the source element; saveCompositionTemplate's
    // normalizeTemplateControls then re-derives the label from the new name so
    // it persists (control.label is always derived, never stored directly).
    // Then the element properties set in the modal's More fields.
    const updatedSource = applySourcePropertyOverrides(
      applyLabelOverridesToSource(
        getSourceForSave
          ? getSourceForSave(elementParts, action)
          : composition.source,
        overrideValues,
      ),
      sourceOverrides,
    );

    // Persist the current on-screen placement (position + size) into the active
    // aspect ratio's config so it survives re-applying the template. The review
    // modal lets the user exclude position and/or scale — excluded parts keep
    // the config values already saved on the template.
    const placement = getCurrentAspectPlacement?.();
    const includePlacement =
      placement && (placementParts.position || placementParts.scale);
    const compositionToSave = {
      ...composition,
      source: updatedSource,
      ...(includePlacement
        ? {
            [placement.key]: {
              ...(composition[placement.key] ?? {}),
              ...(placementParts.position
                ? {
                    positionX: placement.config.positionX,
                    positionY: placement.config.positionY,
                  }
                : {}),
              // Scale is captured per axis; `scale` is dual-written for clients
              // still on the previous bundle. Guarded on scaleX because the
              // capture omits all three when a scale can't be derived (e.g. a
              // fullscreen composition), and the existing config must survive.
              ...(placementParts.scale && placement.config.scaleX !== undefined
                ? {
                    scale: placement.config.scale,
                    scaleX: placement.config.scaleX,
                    scaleY: placement.config.scaleY,
                  }
                : {}),
            },
          }
        : {}),
    };

    const response = await addUpdateFavoriteCompositionTemplate(
      compositionToSave,
      newCompositionName,
      updatedControls,
      categoryId,
      subcategoryId,
      action,
      saveMeta
        ? thumbnail => {
            // Skip if the element was repointed again while this rendered.
            const current = getElementMetaObject(
              videoCreator.currentVideo,
              activeElement.source.id,
            );
            if (current?.templateId !== response?.id) return;
            upsertElementMetaObject(
              videoCreator.currentVideo,
              activeElement.source.id,
              { templateThumbnailUrl: thumbnail.url },
            );
          }
        : undefined,
    );

    if (response && onAfterCreate) {
      onAfterCreate(response);
    }

    if (response && saveMeta) {
      // if saved - associate with new composition template id
      upsertElementMetaObject(
        videoCreator.currentVideo,
        activeElement.source.id,
        {
          ...meta,
          compositionControls: updatedControls,
          templateId: response.id,
          // The thumbnail follows once the new one is rendered.
          templateLabel: response.label,
        },
      );
    }

    if (response && action === 'update') {
      onAfterSave?.();
    }

    if (action === 'update') {
      const timeSinceUpdate = new Date().getTime() - updateStart.getTime();
      if (timeSinceUpdate < UPDATE_CLICK_DELAY) {
        setTimeout(() => {
          setUpdating(null);
        }, UPDATE_CLICK_DELAY - timeSinceUpdate);
      } else {
        setUpdating(null);
      }
    }
  };

  // Creator Studio: the whole composition's placement, saved vs on canvas,
  // for the modal's Card block. Read only while the modal is open.
  const currentPlacement = previewOpen
    ? getCurrentAspectPlacement?.()
    : undefined;
  const cardPlacement =
    previewOpen && getCurrentAspectPlacement
      ? {
          before: currentPlacement
            ? composition[currentPlacement.key]
            : undefined,
          after: currentPlacement?.config,
        }
      : undefined;

  const applyRows = applyOpen
    ? buildApplyStandardsRows({
        controls: composition.controls,
        activeElement,
        extraElementData: videoCreator.currentVideo?.extraElementData,
        updatedValues,
        organization: videoCreator.organization,
        story: videoCreator.story,
      })
    : [];

  return (
    <ButtonWrapper>
      <ButtonRow>
        <SaveButton
          // Always available (unless there's no showcase or a save is running):
          // standards can be updated for field labels too, not just changed
          // control values, so we don't gate on pending edits.
          disabled={!videoCreator.organization || !!updating}
          className={!videoCreator.organization ? 'no-showcase' : ''}
          onClick={() =>
            enableApplyStandardsPreview
              ? setPreviewOpen(true)
              : handleSaveTemplate('update', composition.label)
          }
          style={{ padding: '16px 10px' }}
        >
          Set as Graphic Standard
        </SaveButton>
        <InfoAnchor
          data-tooltip-id="standards-info"
          data-tooltip-content={UPDATE_STANDARDS_TIP}
          aria-label="What does Set as Graphic Standard do?"
        >
          <InfoIconSmall />
        </InfoAnchor>
      </ButtonRow>
      {enableApplyStandardsPreview && (
        <ButtonRow>
          <ApplyButton
            disabled={!videoCreator.organization || !editorStore || !!updating}
            className={!videoCreator.organization ? 'no-showcase' : ''}
            onClick={() => setApplyOpen(true)}
          >
            Reset to Graphic Standard
          </ApplyButton>
          <InfoAnchor
            data-tooltip-id="standards-info"
            data-tooltip-content={APPLY_STANDARDS_TIP}
            aria-label="What does Reset to Graphic Standard do?"
          >
            <InfoIconSmall />
          </InfoAnchor>
        </ButtonRow>
      )}
      <SaveNewButton
        disabled={
          !videoCreator.organization ||
          (!updatedValues.length && !positionDirty) ||
          !!updating
        }
        className={!videoCreator.organization ? 'no-showcase' : ''}
        onClick={() => {
          setModalAction(composition.isGlobalPreset ? 'create' : 'save_as');
        }}
      >
        Save New Template WIP
      </SaveNewButton>
      <NoShowcaseMessage className="no-showcase">
        A story needs to be part of a showcase to have default settings
        available.
      </NoShowcaseMessage>
      <Tooltip
        id="standards-info"
        place="top"
        noArrow
        opacity={1}
        // pre-line renders the tips' bullet lines; content is still plain text.
        style={{ ...PARAGRAPH_TOOLTIP_STYLE, whiteSpace: 'pre-line' }}
      />
      {modalAction && (
        <SaveNameModal
          closeModal={() => setModalAction(null)}
          onConfirm={(newCompositionName: string) =>
            handleSaveTemplate(modalAction, newCompositionName)
          }
          onCancel={() => setModalAction(null)}
          confirmText={modalAction === 'update' ? 'Update' : 'Save'}
          cancelText="Cancel"
          headingText={
            modalAction === 'update'
              ? 'Update Template'
              : 'Save Composition Template'
          }
          initialName={composition.label}
        />
      )}
      {previewOpen && (
        <UpdateStandardsModal
          composition={composition}
          updatedValues={updatedValues}
          organization={videoCreator.organization}
          story={videoCreator.story}
          positionDirty={positionDirty}
          isPullQuote={!saveMeta}
          elementLayout={elementLayout}
          cardPlacement={cardPlacement}
          standardSource={standardSource}
          onCancel={() => setPreviewOpen(false)}
          onApply={(
            editedValues,
            placementParts,
            elementParts,
            sourceOverrides,
          ) => {
            setPreviewOpen(false);
            trackUpdateStandardsApplied({
              surface: saveMeta ? 'creator_studio' : 'pull_quotes',
              controlCount: editedValues.length,
            });
            void handleSaveTemplate(
              'update',
              composition.label,
              editedValues,
              placementParts,
              elementParts,
              sourceOverrides,
            );
          }}
        />
      )}
      {applyOpen && editorStore && (
        <ApplyStandardsModal
          rows={applyRows}
          onCancel={() => setApplyOpen(false)}
          onConfirm={() => {
            setApplyOpen(false);
            applyTemplateStandardsToElement({
              elementId: activeElement.source.id || '',
              editorStore,
              controls: composition.controls,
              organization: videoCreator.organization,
              story: videoCreator.story,
            });
            trackApplyStandardsApplied({
              surface: saveMeta ? 'creator_studio' : 'pull_quotes',
              changedCount: applyRows.filter(r => r.changed).length,
            });
            videoCreator.showToast({
              state: 'success',
              message: 'Card reset to the graphic standard',
              delay: 3000,
            });
          }}
        />
      )}
    </ButtonWrapper>
  );
};

const SaveButton = styled(SaveButtonKaraokeBase)`
  padding: 16px 10px;
  flex-basis: 100%;
  max-width: 100%;

  /* The Karaoke base flattens opacity to 1, so a disabled button reads as
     enabled. Restore a clear disabled look (dimmed + not-allowed); the
     :disabled selector out-specifies that flat opacity. */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:disabled:hover ~ .no-showcase {
    display: none;
  }

  &:disabled:hover.no-showcase ~ .no-showcase {
    display: flex;
  }
`;

const SaveNewButton = styled(SaveButton)`
  /* De-emphasized: this flow isn't reviewed/finalized yet (WIP). */
  border: 1px dashed var(--border);
  background-color: transparent;
  color: var(--text-muted);

  &:disabled:hover ~ .no-showcase {
    display: none;
  }

  &:disabled:hover.no-showcase ~ .no-showcase {
    display: flex;
  }
`;

const ButtonWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--spacing-sm);
  margin-bottom: -15px;
  margin-top: var(--spacing-xl);
`;

// "Reset to Graphic Standard" enforces the template's saved standards onto the
// current card (template → composition) — a different action from the two green
// template-write buttons, so it reads as a neutral full-width button below them.
const ApplyButton = styled(SaveButton)`
  flex-basis: 100%;
  max-width: 100%;
  border: 1px solid var(--border);
  background-color: transparent;
  color: var(--foreground);

  &:hover:not(:disabled) {
    border-color: var(--text-muted);
  }
`;

// User-facing explanations of the two inverse standards actions. Each spells
// out the direction, what will happen, and what stays untouched (PLAT-11432).
const UPDATE_STANDARDS_TIP = [
  "• Saves this card's current settings as this graphic's standard",
  '• New cards of this graphic will start with these settings',
  '• You review every change and can adjust it before anything is saved',
  '• Only the standard is updated — this card and existing videos stay as they are',
].join('\n');

const APPLY_STANDARDS_TIP = [
  "• Returns this card to the graphic's saved standard settings",
  '• A preview shows exactly what will change before you confirm',
  '• Only this card changes — the saved standard is not modified',
].join('\n');

// Row holding a full-width button with its info icon overlaid at the right edge.
const ButtonRow = styled.div`
  position: relative;
  flex-basis: 100%;
  max-width: 100%;
  display: flex;
`;

// The info affordance is a SIBLING of the button (not a child) so its hover /
// tooltip still fires when the button is disabled, and it sits above the button
// so clicking the icon doesn't trigger the button's action.
const InfoAnchor = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  cursor: help;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--foreground);
  }
`;
